/**
 * Patient Portal Links Component
 * Shows clinic admins the URLs they can share with patients for registration and login
 * Includes QR codes and copy-to-clipboard functionality
 */
import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { 
  Link2, 
  Copy, 
  Check, 
  QrCode, 
  ExternalLink, 
  Users, 
  UserPlus,
  Globe,
  Smartphone,
  Mail,
  Share2
} from 'lucide-react';
import { toast } from 'sonner';
import QRCode from 'qrcode';

const PatientPortalLinks = ({ clinic }) => {
  const [copiedField, setCopiedField] = useState(null);
  const [qrCodes, setQrCodes] = useState({});
  const [showQR, setShowQR] = useState(null);

  // Production domain for patient portal links
  // Priority: 1) Clinic's custom domain, 2) Production domain, 3) Current origin (for dev)
  const productionDomain = 'https://mymedmember.com';
  const baseUrl = clinic?.custom_domain 
    ? `https://${clinic.custom_domain}` 
    : (process.env.NODE_ENV === 'production' ? productionDomain : window.location.origin);
  
  // Generate portal URLs based on clinic subdomain
  const clinicSubdomain = clinic?.subdomain || clinic?.slug || 'your-clinic';
  
  const portalUrls = {
    patientLogin: `${productionDomain}/patient/${clinicSubdomain}`,
    patientRegister: `${productionDomain}/register-patient?clinic=${clinicSubdomain}`,
    friendlyRegister: `${productionDomain}/join/${clinicSubdomain}`,
    universalLogin: `${productionDomain}/login`,
    memberVerification: `${productionDomain}/verify`
  };

  // Generate QR codes for URLs
  useEffect(() => {
    const generateQRCodes = async () => {
      const codes = {};
      for (const [key, url] of Object.entries(portalUrls)) {
        try {
          codes[key] = await QRCode.toDataURL(url, {
            width: 200,
            margin: 2,
            color: {
              dark: '#0d9488',
              light: '#ffffff'
            }
          });
        } catch (err) {
          console.error('QR generation error:', err);
        }
      }
      setQrCodes(codes);
    };
    generateQRCodes();
  }, [clinicSubdomain]);

  const copyToClipboard = async (text, fieldName) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedField(fieldName);
      toast.success('Copied to clipboard!');
      setTimeout(() => setCopiedField(null), 2000);
    } catch (err) {
      toast.error('Failed to copy');
    }
  };

  const LinkCard = ({ title, description, url, icon: Icon, fieldKey, badge }) => (
    <div className="border rounded-lg p-4 space-y-3 hover:border-teal-300 transition-colors">
      <div className="flex items-start justify-between">
        <div className="flex items-center gap-2">
          <div className="p-2 bg-teal-50 rounded-lg">
            <Icon className="h-5 w-5 text-teal-600" />
          </div>
          <div>
            <h4 className="font-medium text-gray-900 flex items-center gap-2">
              {title}
              {badge && (
                <Badge variant="secondary" className="text-xs">
                  {badge}
                </Badge>
              )}
            </h4>
            <p className="text-xs text-gray-500">{description}</p>
          </div>
        </div>
      </div>
      
      <div className="flex items-center gap-2">
        <Input 
          value={url} 
          readOnly 
          className="text-sm bg-gray-50 font-mono text-gray-600"
        />
        <Button
          variant="outline"
          size="icon"
          onClick={() => copyToClipboard(url, fieldKey)}
          className="shrink-0"
        >
          {copiedField === fieldKey ? (
            <Check className="h-4 w-4 text-green-600" />
          ) : (
            <Copy className="h-4 w-4" />
          )}
        </Button>
        <Button
          variant="outline"
          size="icon"
          onClick={() => setShowQR(showQR === fieldKey ? null : fieldKey)}
          className="shrink-0"
        >
          <QrCode className="h-4 w-4" />
        </Button>
        <Button
          variant="outline"
          size="icon"
          onClick={() => window.open(url, '_blank')}
          className="shrink-0"
        >
          <ExternalLink className="h-4 w-4" />
        </Button>
      </div>

      {/* QR Code Display */}
      {showQR === fieldKey && qrCodes[fieldKey] && (
        <div className="flex flex-col items-center p-4 bg-white border rounded-lg">
          <img src={qrCodes[fieldKey]} alt={`QR Code for ${title}`} className="w-40 h-40" />
          <p className="text-xs text-gray-500 mt-2">Scan to open {title.toLowerCase()}</p>
          <Button
            variant="outline"
            size="sm"
            className="mt-2"
            onClick={() => {
              const link = document.createElement('a');
              link.download = `${clinicSubdomain}-${fieldKey}-qr.png`;
              link.href = qrCodes[fieldKey];
              link.click();
            }}
          >
            Download QR Code
          </Button>
        </div>
      )}
    </div>
  );

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Link2 className="h-5 w-5 text-teal-600" />
          Patient Portal Links
        </CardTitle>
        <CardDescription>
          Share these links with your patients for easy access to registration and login
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Primary Links */}
        <div className="space-y-3">
          <Label className="text-sm font-medium text-gray-700">Patient Access Links</Label>
          
          <LinkCard
            title="Patient Login"
            description="Clinic-branded login page for your patients"
            url={portalUrls.patientLogin}
            icon={Users}
            fieldKey="patientLogin"
            badge="Recommended"
          />

          <LinkCard
            title="Patient Registration"
            description="New patient signup with your clinic pre-selected"
            url={portalUrls.friendlyRegister}
            icon={UserPlus}
            fieldKey="friendlyRegister"
            badge="Friendly URL"
          />
        </div>

        {/* Divider */}
        <div className="border-t my-4" />

        {/* Secondary Links */}
        <div className="space-y-3">
          <Label className="text-sm font-medium text-gray-500">Alternative Links</Label>
          
          <LinkCard
            title="Registration with Parameters"
            description="Alternative registration URL with clinic parameter"
            url={portalUrls.patientRegister}
            icon={Globe}
            fieldKey="patientRegister"
          />

          <LinkCard
            title="Universal Login"
            description="General login page (patients select clinic)"
            url={portalUrls.universalLogin}
            icon={Smartphone}
            fieldKey="universalLogin"
          />
        </div>

        {/* Tips Section */}
        <div className="mt-6 p-4 bg-blue-50 rounded-lg border border-blue-200">
          <h4 className="font-medium text-blue-800 mb-2 flex items-center gap-2">
            <Share2 className="h-4 w-4" />
            Sharing Tips
          </h4>
          <ul className="text-sm text-blue-700 space-y-1">
            <li>• Add the <strong>Patient Login</strong> link to your website</li>
            <li>• Include the <strong>Registration</strong> link in welcome emails</li>
            <li>• Print QR codes for your reception area</li>
            <li>• Use the friendly URL (<code>/join/{clinicSubdomain}</code>) on business cards</li>
          </ul>
        </div>

        {/* Email Template */}
        <div className="mt-4 p-4 bg-gray-50 rounded-lg border">
          <div className="flex items-center justify-between mb-2">
            <h4 className="font-medium text-gray-800 flex items-center gap-2">
              <Mail className="h-4 w-4" />
              Email Template
            </h4>
            <Button
              variant="outline"
              size="sm"
              onClick={() => copyToClipboard(
                `Welcome to ${clinic?.name || 'our clinic'}!\n\nTo access your patient portal, please visit:\n${portalUrls.patientLogin}\n\nIf you're a new patient, register here:\n${portalUrls.friendlyRegister}\n\nThank you for choosing us!`,
                'emailTemplate'
              )}
            >
              {copiedField === 'emailTemplate' ? (
                <>
                  <Check className="h-4 w-4 mr-1" />
                  Copied!
                </>
              ) : (
                <>
                  <Copy className="h-4 w-4 mr-1" />
                  Copy Template
                </>
              )}
            </Button>
          </div>
          <pre className="text-xs text-gray-600 whitespace-pre-wrap bg-white p-3 rounded border">
{`Welcome to ${clinic?.name || 'our clinic'}!

To access your patient portal, please visit:
${portalUrls.patientLogin}

If you're a new patient, register here:
${portalUrls.friendlyRegister}

Thank you for choosing us!`}
          </pre>
        </div>
      </CardContent>
    </Card>
  );
};

export default PatientPortalLinks;
